import { z } from "zod";
import { optNum } from "./zodNum";
import { isoDate } from "./dates";

/**
 * Alapértelmezett indexsorok (éves átlagos változás %-ban). A 2019–2024-es értékek a KSH és az
 * Eurostat közzétett adatai; a 2025-ös értékek előzetesek – élesítés előtt ellenőrizni kell.
 */
export const DEFAULT_INDEX_SERIES: { key: string; name: string; description: string; values: { period: string; ratePct: number; source: string }[] }[] = [
  {
    key: "KSH_CPI",
    name: "KSH fogyasztói árindex",
    description: "Magyarország, éves átlagos változás (előző év = 100)",
    values: [
      { period: "2019", ratePct: 3.4, source: "KSH" },
      { period: "2020", ratePct: 3.3, source: "KSH" },
      { period: "2021", ratePct: 5.1, source: "KSH" },
      { period: "2022", ratePct: 14.5, source: "KSH" },
      { period: "2023", ratePct: 17.6, source: "KSH" },
      { period: "2024", ratePct: 3.7, source: "KSH" },
      { period: "2025", ratePct: 4.4, source: "KSH – előzetes, ellenőrizendő" },
    ],
  },
  {
    key: "HICP_EU",
    name: "HICP (EU27)",
    description: "Eurostat harmonizált fogyasztói árindex, Európai Unió, éves átlag",
    values: [
      { period: "2021", ratePct: 2.9, source: "Eurostat" },
      { period: "2022", ratePct: 9.2, source: "Eurostat" },
      { period: "2023", ratePct: 6.4, source: "Eurostat" },
      { period: "2024", ratePct: 2.6, source: "Eurostat" },
      { period: "2025", ratePct: 2.5, source: "Eurostat – előzetes, ellenőrizendő" },
    ],
  },
  {
    key: "MUICP",
    name: "MUICP (euróövezet)",
    description: "Eurostat monetáris unió árindex, euróövezet, éves átlag",
    values: [
      { period: "2021", ratePct: 2.6, source: "Eurostat" },
      { period: "2022", ratePct: 8.4, source: "Eurostat" },
      { period: "2023", ratePct: 5.4, source: "Eurostat" },
      { period: "2024", ratePct: 2.4, source: "Eurostat" },
      { period: "2025", ratePct: 2.1, source: "Eurostat – előzetes, ellenőrizendő" },
    ],
  },
];

export const INDEX_BASIS_LABEL = { ANNUAL_AVG: "Előző év átlaga", MONTH_YOY: "Bázishónap 12 havi változása" } as const;
export const HU_MONTHS = ["január", "február", "március", "április", "május", "június", "július", "augusztus", "szeptember", "október", "november", "december"];

const optPct = optNum(z.coerce.number().min(-50).max(100));

export const indexationClauseSchema = z
  .object({
    effectiveDate: isoDate,
    seriesId: z.string().uuid().nullable().optional(),
    basis: z.enum(["ANNUAL_AVG", "MONTH_YOY"]).default("ANNUAL_AVG"),
    baseMonth: optNum(z.coerce.number().int().min(1).max(12)),
    fixedPct: optPct,
    addPct: optPct,
    floorPct: optPct,
    capPct: optPct,
    allowDecrease: z.boolean().default(false),
    effectiveMonth: z.coerce.number().int().min(1).max(12).default(1),
    effectiveDay: z.coerce.number().int().min(1).max(28).default(1),
    firstDueDate: isoDate.nullable().optional(),
    chargeTypeIds: z.array(z.string().uuid()).default([]),
    retroactiveAllowed: z.boolean().default(true),
    note: z.string().trim().max(1000).nullable().optional(),
  })
  .refine((v) => !!v.seriesId || v.fixedPct != null, { path: ["fixedPct"], message: "Válassz indexet, vagy adj meg fix százalékot" })
  .refine((v) => v.basis !== "MONTH_YOY" || v.baseMonth != null, { path: ["baseMonth"], message: "Add meg a bázishónapot" })
  .refine((v) => v.floorPct == null || v.capPct == null || v.floorPct <= v.capPct, { path: ["capPct"], message: "A plafon nem lehet kisebb a padlónál" });
export type IndexationClauseInput = z.infer<typeof indexationClauseSchema>;

export const endClauseSchema = z.object({ effectiveDate: isoDate });

export const indexValueSchema = z.object({
  period: z.string().regex(/^\d{4}(-(0[1-9]|1[0-2]))?$/, "Időszak: ÉÉÉÉ vagy ÉÉÉÉ-HH"),
  ratePct: z.coerce.number().min(-50).max(100),
  source: z.string().trim().max(120).nullable().optional(),
});

export const applyIndexationSchema = z.object({
  dueDate: isoDate,
  /** Amitől az új díj érvényes; üres = a hatálynap (visszamenőleg, ha a záradék engedi) */
  effectiveDate: isoDate.optional(),
  /** Kézi felülírás (pl. a bérlővel egyeztetett mérték) – indoklással */
  ratePct: z.coerce.number().min(-50).max(100).optional(),
  note: z.string().trim().max(1000).nullable().optional(),
  sendNotice: z.boolean().default(false),
});

export const waiveIndexationSchema = z.object({ dueDate: isoDate, reason: z.string().trim().min(3, "Írd le, miért marad el").max(1000) });
